import { computed, toRef } from 'vue';
import {
  tc,
  useFlowRuleName,
  useI18n,
  usePlanLimits,
  type PlanLimitsProp,
} from '@/composables';
import { useFlowDialogFieldErrors } from '@/composables/useFlowDialogFieldErrors';
import { $emitter } from '@/services/emitter';
import { MoveDirectionEnum } from '@/services/models/Enums';
import { useStore } from '@/store';

type CampaignFlowRuleProps = {
  ruleIndex: number;
  isDisabledWeight: boolean;
  rule: Record<string, unknown> & {
    name?: string;
    status?: string;
    conditions?: Record<string, unknown>;
    paths?: Array<Record<string, unknown>>;
    lastPathNumber?: number;
    default?: boolean;
  };
  offerOptions: Offer[];
  landingOptions: Landing[];
  form: Record<string, unknown> & {
    flowInline: { rules: unknown[] };
  };
  countryId: string;
  landings: unknown[];
  offers: unknown[];
  errorConditionPrefix: string[];
  dialogRepositionParam: string;
  errorsRaw: unknown[][] | null;
};

const canMoveUp = (index: number) => index > 1;

const canMoveDown = (index: number, items: unknown[]) => index < items.length - 1;

const getSwapIndexes = (
  index: number,
  items: unknown[],
  direction: MoveDirectionEnum = MoveDirectionEnum.up,
) => {
  if (direction === MoveDirectionEnum.up) {
    if (!canMoveUp(index)) return;
    return { a: index - 1, b: index };
  }

  if (!canMoveDown(index, items)) return;
  return { a: index, b: index + 1 };
};

const swap = (items: unknown[], ids: { a: number; b: number }) => {
  const temp = items[ids.a];
  items[ids.a] = items[ids.b];
  items[ids.b] = temp;
};

export const useCampaignFlowRule = (
  props: CampaignFlowRuleProps,
  onAddNewRuleEmit: (rule: CampaignFlowRuleProps['rule']) => void,
) => {
  const { t } = useI18n();
  const store = useStore();
  const planLimitsControl = usePlanLimits();
  const { buildFlowRuleName, isOppositeFlowRulePredicate } = useFlowRuleName();

  const moveDirectionEnum = {
    up: MoveDirectionEnum.up,
    down: MoveDirectionEnum.down,
  };

  const hasPlanLimitsRestriction = (limitKey: PlanLimitsProp) =>
    planLimitsControl.hasPlanLimitsRestriction(limitKey);

  const conditionPlanLimits = (condition: string) =>
    planLimitsControl.conditionPlanLimits(condition);

  const reportingFlowRulePathEnabled = computed(
    () => store.user.featureFlags?.reportingFlowRulePath?.enabled,
  );

  const errorCondition = computed(() => [
    ...props.errorConditionPrefix,
    'rules',
    props.ruleIndex + 1,
  ]);

  const {
    isNoConditionError,
    isAtLeastOneActivePathError,
    isAnyRuleHasError,
  } = useFlowDialogFieldErrors({
    errorsRaw: toRef(props, 'errorsRaw'),
    errorCondition,
  });

  const showPlanLimitsWarning = computed(() =>
    Boolean(
      Object.keys(props.rule.conditions || {}).find((condition) =>
        hasPlanLimitsRestriction(conditionPlanLimits(condition)),
      ),
    ),
  );

  const ruleConditions = computed(() => {
    const { conditions } = props.rule;
    const item = { conditions: conditions || {} };
    const result = buildFlowRuleName(item, false);

    if (!Array.isArray(result)) {
      return [];
    }

    if (result.length > 1) {
      return [
        result[0],
        {
          black: true,
          name: ` (${tc('+{0} conditions', result.length - 1, [result.length - 1])})`,
        },
      ];
    }

    return result;
  });

  const moveRule = (
    index: number,
    items: unknown[],
    direction: MoveDirectionEnum = MoveDirectionEnum.up,
  ) => {
    const ids = getSwapIndexes(index, items, direction);
    if (ids) {
      swap(items, ids);
    }
  };

  const addNewPath = (rule: CampaignFlowRuleProps['rule']) => {
    const lastItem = (rule.lastPathNumber || rule.paths?.length || 0) + 1;
    rule.lastPathNumber = lastItem;
    rule.paths?.push({
      name: `${t('Path')} ${lastItem}`,
      weight: 100,
      status: 'active',
      redirectMode: '302',
      directLinking: false,
      landings: [],
      offers: [],
    });
  };

  const deleteRule = () => {
    $emitter.emit('campaign-delete-rule', props.ruleIndex);
  };

  const onAddNewRule = (rule: CampaignFlowRuleProps['rule']) => {
    onAddNewRuleEmit(rule);
  };

  return {
    t,
    moveDirectionEnum,
    reportingFlowRulePathEnabled,
    showPlanLimitsWarning,
    ruleConditions,
    isOppositeFlowRulePredicate,
    isNoConditionError,
    isAtLeastOneActivePathError,
    isAnyRuleHasError,
    moveRule,
    addNewPath,
    deleteRule,
    onAddNewRule,
  };
};
